import { useEffect, useCallback } from 'react';
import { AuthenticatedImage } from './AuthenticatedImage.jsx';

/**
 * Full-screen modal viewer for a screenshot. The caller controls which
 * screenshot is shown via `currentIndex`; the lightbox calls `onNavigate`
 * with the new index when the user clicks prev/next or presses ←/→.
 *
 * Closes on backdrop click, X button, or Esc.
 */
export function ScreenshotLightbox({ open, screenshots, currentIndex, onClose, onNavigate }) {
    const total = screenshots?.length || 0;
    const current = open && total > 0 ? screenshots[currentIndex] : null;

    const goPrev = useCallback(() => {
        if (currentIndex > 0) onNavigate(currentIndex - 1);
    }, [currentIndex, onNavigate]);

    const goNext = useCallback(() => {
        if (currentIndex < total - 1) onNavigate(currentIndex + 1);
    }, [currentIndex, total, onNavigate]);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose, goPrev, goNext]);

    if (!open || !current) return null;

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.85)',
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                padding: 18,
            }}
        >
            {/* Top bar */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    color: '#fff',
                    paddingBottom: 12,
                    flexShrink: 0,
                }}
            >
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {current.active_app || '(no app data)'}
                    </div>
                    <div style={{ fontSize: 12, color: '#cdcabd', marginTop: 2 }}>
                        {new Date(current.timestamp).toLocaleString()}
                        {' · '}
                        {currentIndex + 1} of {total}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        color: '#fff',
                        border: 0,
                        borderRadius: 6,
                        padding: '6px 14px',
                        fontSize: 13,
                        cursor: 'pointer',
                    }}
                >
                    Close
                </button>
            </div>

            {/* Image area with prev/next arrows */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    minHeight: 0,
                }}
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <NavArrow direction="left" disabled={currentIndex === 0} onClick={goPrev} />
                <AuthenticatedImage
                    key={current.id}
                    screenshotId={current.id}
                    alt={current.active_app}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 6,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    }}
                />
                <NavArrow direction="right" disabled={currentIndex >= total - 1} onClick={goNext} />
            </div>
        </div>
    );
}

function NavArrow({ direction, disabled, onClick }) {
    const isLeft = direction === 'left';
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            aria-label={isLeft ? 'Previous' : 'Next'}
            style={{
                position: 'absolute',
                [isLeft ? 'left' : 'right']: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.15)',
                color: '#fff',
                border: 0,
                borderRadius: '50%',
                width: 44,
                height: 44,
                fontSize: 20,
                fontWeight: 700,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.3 : 1,
                userSelect: 'none',
            }}
        >
            {isLeft ? '‹' : '›'}
        </button>
    );
}
